import { MyScriptLocaleType, MyScriptMimeType, MyScriptRecognitionType } from "../../../nl-lib3-common/typedef/myscript-types/myscript-types";
import { MauiRelaySocket, MauiToJsBridge, sleep } from "../nl-lib-maui";
import { IDiscoveredDevices } from "../../../nl-lib3-common/interfaces/IDiscoveredDevices";
import { MauiToJsEventName } from "../nl-lib-maui/maui-bridge/maui-to-js-types";
import { PEN_CHARACTERISTICS_NOTIFICATION_UUID_128, PEN_CHARACTERISTICS_NOTIFICATION_UUID_16_STR, PEN_CHARACTERISTICS_WRITE_UUID_128, PEN_CHARACTERISTICS_WRITE_UUID_16_STR, PEN_SERVICE_UUID_128, PEN_SERVICE_UUID_16_STR } from "../nl-lib-pensdk/pencomm/pencomm_const";
import { NativeConversionResult } from "./ImportFromFileTypes";
import { NeoStroke } from "../../../nl-lib3-common/neostroke/NeoStroke";
import { NeoDot } from "../../../nl-lib3-common/neostroke/dot/NeoDot";
import { INcodeRange, IUSBConnectedPen, ReadOfflineStrokesResponse, StrokesBySobpDto } from "@/neolab-libs/nl-lib3-common/interfaces/IUSBConnectedPen";
import { IPageSOBP } from "@/neolab-libs/nl-lib3-common";

export enum AcrobatReaderPrintErrorCode {
	Success = 0,
	PDFFileNotFound = 1,
	AcrobatAppNotFound = 2,
	ErrorWhilePrinting = 3,

	AcrobatAppCreateFailed = 4,
	AcrobatAVDocCreateFailed = 5,
	AcrobatPDDocCreateFailed = 6,
	AcrobatPrintPagesSilentExFailed = 7,

	JsInteropError = 8,

	OtherError = 99
}

export class NativeMethods {
	/**
	 *
	 * @param fileName
	 * @param uint8Array
	 * @param separators 기본으로는 ##문제, ##지시문, ##지문, ##문항, ##끝
	 * @returns
	 */
	public static async GetQuestionsHtmlFromWordDocBytes(
		fileName: string,
		uint8Array: Uint8Array,
		separators?: {
			questionStartSeparator?: string,
			instructionSeparator?: string,
			passageSeparator?: string,
			subQuestionSeparator?: string,
			answerSeparator?: string,
			questionEndSeparator?: string
		}) {

		let {
			questionStartSeparator = "##문제",
			instructionSeparator = "##지시문",
			passageSeparator = "##지문",
			subQuestionSeparator = "##문항",
			answerSeparator = "##답안",
			questionEndSeparator = "##끝"
		} = separators ?? {}

		const result = await MauiRelaySocket.instance.bridgeInvokeMethodAsync<NativeConversionResult>(
			"GetQuestionsHtmlFromWordDocBytes",
			fileName,
			uint8Array,
			questionStartSeparator,
			instructionSeparator,
			passageSeparator,
			subQuestionSeparator,
			answerSeparator,
			questionEndSeparator
		);

		return result?.questionares;
	}

	/**
	 * Acrobat Reader를 통해 PDF를 인쇄합니다.
	 * @param fileName
	 * @param uint8Array
	 * @returns
	 */
	public static async PrintPDFThroughAcrobatReader(
		fileName: string,
		uint8Array: Uint8Array,
		acrobatExePath = null as string
	) {
		const ret = await MauiRelaySocket.instance.bridgeInvokeMethodAsync<{ success: boolean, message: string, errorCode: AcrobatReaderPrintErrorCode }>(
			"PrintPDFThroughAcrobatReader",
			fileName,
			uint8Array,
			acrobatExePath
		);

		return ret;
	}

	/**
	 * Acrobat Reader가 설치된 경로를 반환합니다. 복수개가 있으면 우선순위가 높은 순으로 반환합니다.
	 * @returns
	 */
	public static async FindAcrobatExecutables() {
		const ret = await MauiRelaySocket.instance.bridgeInvokeMethodAsync<
			{
				exeName: string,
				folderPath: string,
				priority: number
			}[]
		>("FindAcrobatExecutables");

		return ret;
	}


	/**
	 * Word가 설치되어 있는지 확인합니다.
	 * @returns
	 */
	public static async IsWordInstalled() {
		const ret = await MauiRelaySocket.instance.bridgeInvokeMethodAsync<boolean>("IsWordInstalled");

		return ret;
	}

	public static async IsAutomationIsntalled(echoStr = null as string) {
		const e = echoStr ?? "Hello, World!";
		try {
			const ret = await MauiRelaySocket.instance.bridgeInvokeMethodAsync<string>("Echo", e);

			if (ret === e) {
				return true;
			}
		} catch (e) {
			console.error(e);
		}

		return false

	}

	public static async GetAutomationVersion() {
		try {
			const ret = await MauiRelaySocket.instance.bridgeInvokeMethodAsync<string>("GetVersion");
			return ret;

		} catch (e) {
			console.error(e);
		}

		return null;
	}

	public static async RequestRecognizeWithTask(locale: MyScriptLocaleType, outputMimeType: MyScriptMimeType, inkstorePageJson: string, typeString: MyScriptRecognitionType) {
		const ret = await MauiRelaySocket.instance.bridgeInvokeMethodAsync<Uint8Array>(
			"RequestRecognizeWithTask",
			locale,
			outputMimeType,
			inkstorePageJson,
			typeString
		);

		if (!ret) return null;

		// convert to unicode string
		const decoder = new TextDecoder("utf-8");
		const decodedString = decoder.decode(ret);

		try {
			const result = JSON.parse(decodedString);
			return result;
		}
		catch (e) {
			console.error(e);
		}
		return decodedString;
	}

	public static async StartScan(args: {
		namePrefix?: string,
		timeout?: number,
		callBackFunc: (args: { devices: IDiscoveredDevices[] }) => void
	}) {
		const { namePrefix = null, timeout = 120, callBackFunc } = args;
		await MauiRelaySocket.instance.bridgeInvokeMethodAsync("SetUuidConstants",
			PEN_SERVICE_UUID_16_STR,
			PEN_CHARACTERISTICS_WRITE_UUID_16_STR,
			PEN_CHARACTERISTICS_NOTIFICATION_UUID_16_STR,
			PEN_SERVICE_UUID_128,
			PEN_CHARACTERISTICS_WRITE_UUID_128,
			PEN_CHARACTERISTICS_NOTIFICATION_UUID_128
		)
		const ret = await MauiRelaySocket.instance.bridgeInvokeMethodAsync<{ success: boolean, maxSamplingInterval: number, minSamplingInterval: number }>("StartScan");

		MauiToJsBridge.instance.setEventListener(MauiToJsEventName.onDeviceDiscoveringUpdate, callBackFunc);

		return ret;
	}


	public static async StopScan() {
		await MauiRelaySocket.instance.bridgeInvokeMethodAsync("StopScan");
		// await sleep(2000);
	}

	public static async DisconnectAllPens() {
		await MauiRelaySocket.instance.bridgeInvokeMethodAsync("DisconnectAll");
	}

	public static async GetNumConnectedPens() {
		const pens = await MauiRelaySocket.instance.bridgeInvokeMethodAsync("GetNumConnectedPens");
		return pens;
	}

	// 2025-08-27 USB 연결용 함수 정의
	public static async StartReadingUSBConnections(args: {
		callBackFunc: (args: { devices: IUSBConnectedPen[] }) => void
	}) {

		const { callBackFunc } = args;

		MauiToJsBridge.instance.setEventListener(MauiToJsEventName.onUSBConnectedUpdate, callBackFunc);

		await MauiRelaySocket.instance.bridgeInvokeMethodAsync("StartReadingUSBConnections");

	}

	public static async StopReadingUSBConnections() {

		MauiToJsBridge.instance.removeEventListener(MauiToJsEventName.onUSBConnectedUpdate);		

		// close socket session		
		await MauiRelaySocket.instance.bridgeInvokeMethodAsync("StopReadingUSBConnections");
	}

	public static async ReadOfflineStrokes(mac:String, range: INcodeRange, assignedStudentsBook: IPageSOBP): Promise<[StrokesBySobpDto[], StrokesBySobpDto[], string[]]> {

		// Keep only fields you actually need; JSON.stringify drops `undefined` keys.
		const payload = JSON.stringify({
			start: {
				section: range.start.section,
				owner:   range.start.owner,
				book:    range.start.book,
				page:    range.start.page,
			},
			end: {
				section: range.end.section,
				owner:   range.end.owner,
				book:    range.end.book,
				page:    range.end.page,
			},
			assignedStudentsBook: {
				section: assignedStudentsBook.section,
				owner:   assignedStudentsBook.owner,
				book:    assignedStudentsBook.book,
				page: 0
			}
		});

		const ret = await MauiRelaySocket.instance.bridgeInvokeMethodAsync("ReadOfflineStrokes", mac, payload);
		let strokes;
		let fileNames;
		let studentCodeStrokes;
		try {
			const response = this.parseReadOfflineStrokesResponse(ret);

			if (response.success == false) {
				throw response.error;
			}
			const [itemsDeserialized, studentCodeItemsDeserialized] = NativeMethods.deserializeStrokesBySobp({ items: response.items, studentCodeItems: response.studentCodeItems });
			strokes = itemsDeserialized;
			fileNames = response.fileNames;
			studentCodeStrokes = studentCodeItemsDeserialized;
			
		} catch (e) {
			const msg = e instanceof Error ? e.message : String(e);
			throw new Error(`parseReadOfflineStrokesResponse failed: ${msg}`);
		} finally {
			return [strokes, studentCodeStrokes, fileNames];
		}
		
		
	}

	public static async deleteFile(mac:String, fileNames: string[]) {
		try {

			const fileNamesSeparatedByComma = fileNames.join(",");
			await MauiRelaySocket.instance.bridgeInvokeMethodAsync("DeleteFile", mac, fileNamesSeparatedByComma);
			
		} catch (e) {
			const msg = e instanceof Error ? e.message : String(e);
			throw new Error(`deleteFile failed: ${msg}`);
		} finally {
			return;
		}
	}

	// Minimal, safe parser (accepts JSON string or object)
	private static parseReadOfflineStrokesResponse(input: string | ReadOfflineStrokesResponse): { success: boolean; error?: string; items: StrokesBySobpDto[], studentCodeItems: StrokesBySobpDto[], fileNames: string[] } {
		try {
			const obj: any = typeof input === "string" ? JSON.parse(input) : input;

			// Preserve explicit false by checking type first
			const success =
			(typeof obj?.Success === "boolean" ? obj.Success : undefined) ??
			(typeof obj?.success === "boolean" ? obj.success : false);

			const error =
			(typeof obj?.Error === "string" ? obj.Error : undefined) ??
			(typeof obj?.error === "string" ? obj.error : undefined);

			const rawItems: any[] =
			(Array.isArray(obj?.Items) ? obj.Items : undefined) ??
			(Array.isArray(obj?.items) ? obj.items : []) ;

			const studentCodeRawItems: any[] =
			(Array.isArray(obj?.StudentCodeItems) ? obj.StudentCodeItems : undefined) ??
			(Array.isArray(obj?.studentCodeItems) ? obj.studentCodeItems : []) ;

			const fileNames: string[] =
			(Array.isArray(obj?.FileNames) ? obj.FileNames : undefined) ??
			(Array.isArray(obj?.fileNames) ? obj.fileNames : []) ;

			// Normalize each item to PascalCase fields (works with either casing in input)
			const items = rawItems.map((it: any) => ({
			Sobp: it?.Sobp ?? it?.sobp,
			Strokes: Array.isArray(it?.Strokes) ? it.Strokes :
					Array.isArray(it?.strokes) ? it.strokes : []
			})) as StrokesBySobpDto[];
			
			const studentCodeItems = studentCodeRawItems.map((it: any) => ({
			Sobp: it?.Sobp ?? it?.sobp,
			Strokes: Array.isArray(it?.Strokes) ? it.Strokes :
					Array.isArray(it?.strokes) ? it.strokes : []
			})) as StrokesBySobpDto[];

			return { success, error, items, studentCodeItems, fileNames };
		} catch (e) {
			const msg = e instanceof Error ? e.message : String(e);
			throw new Error(`parseReadOfflineStrokesResponse failed: ${msg}`);
		}
	}

	private static deserializeStrokesBySobp(jsonOrObj: string | { items: StrokesBySobpDto[]; studentCodeItems: StrokesBySobpDto[] }) {

		try {
			const parsed: any =
				typeof jsonOrObj === "string"
					? JSON.parse(jsonOrObj)
					: jsonOrObj;

			// Expect an object with { items, studentCodeItems }
			const normalizeArray = (arrLike: any): StrokesBySobpDto[] => {
				if (!Array.isArray(arrLike)) return [] as StrokesBySobpDto[];
				return arrLike.map((item: any) => {
					const sobp = item.sobp ?? item.Sobp;
					const strokesRaw = (item.strokes ?? item.Strokes) ?? [];
					const strokes = Array.isArray(strokesRaw)
						? strokesRaw
							.map(NativeMethods.toNeoStroke)
							.filter((s): s is NeoStroke => s !== undefined)
						: [];
					return { sobp, strokes } as StrokesBySobpDto;
				});
			};

			if (parsed && typeof parsed === "object") {
				const items = normalizeArray((parsed as any).items);
				const studentCodeItems = normalizeArray((parsed as any).studentCodeItems);
				return [items, studentCodeItems] as [StrokesBySobpDto[], StrokesBySobpDto[]];
			}

			return [[], []] as [StrokesBySobpDto[], StrokesBySobpDto[]];
		} catch (err) {
			// TODO: - reporting 

			console.error("deserializeStrokesBySobp failed:", err);
			return [[], []] as [StrokesBySobpDto[], StrokesBySobpDto[]];
		}
	}

	private static toNeoStroke(raw: any): NeoStroke | undefined {
		// Normalize raw first (JSON.parse if it's still a string)
		const props = typeof raw === "string" ? JSON.parse(raw) : raw;

        if (!props || typeof props !== "object") {
			// TODO: - reporting

            return undefined;
        }

		// Explicitly pick out values you need
		const neoStroke = new NeoStroke({
			key: props.key ?? props.originalKey ?? props.uuid,
			cmd: props.cmd,
			originalKey: props.originalKey,
			section: Number(props.section ?? 0),
			owner: Number(props.owner ?? 0),
			book: Number(props.book ?? 0),
			page: Number(props.page ?? 0),
			pageUuid: props.pageUuid,
			noteUuid: props.noteUuid,
			mac: props.mac,
			brushType: props.brushType,
			thickness: 1.0,
			color: props.color,
			status: props.status,
			min_speed: props.min_speed,
			max_speed: props.max_speed,
			multiPage: props.multiPage,
			penTipMode: Number(props.penTipMode ?? 0),
			inputType: props.inputType,
			updated: Number(props.updated ?? 0),
			startTime: Number(props.startTime ?? 0),
			duration: Number(props.duration ?? 0),
			uploadTime: Number(props.uploadTime ?? 0),
			dotArray: Array.isArray(props.dotArray)
			? props.dotArray.map((d: any) => new NeoDot({
				x: Number(d.x),
				y: Number(d.y),
				f: Number(d.f),
				dotType: d.dotType,
				time: Number(d.time),
				deltaTime: Number(d.deltaTime),
				TiltX: Number(d.TiltX ?? d.tiltX),
				TiltY: Number(d.TiltY ?? d.tiltY),
				Twist: Number(d.Twist ?? d.twist),
				}))
			: []
		});

		// dotCount can be derived from dotArray length
		neoStroke.dotCount = neoStroke.dotArray.length;

		return neoStroke;
	}

}

